import { useState } from "react";
import axios from "axios";
import { Mail, Phone, CheckCircle2, FileText, PhoneCall, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLines, Eyebrow } from "../components/Reveal";
import { CONTACT } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 font-sans text-base text-navy placeholder:text-slate-400 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30 transition-colors duration-300";

const businessTypes = [
  "Healthcare",
  "Professional services",
  "Home services / contractor",
  "Beauty, wellness / spa",
  "Retail / e-commerce",
  "Other",
];

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block font-sans text-sm font-semibold text-navy">
      {label}
    </label>
    {children}
  </div>
);

const initial = { name: "", facility_name: "", business_type: "", role: "", email: "", phone: "", concern: "" };

const expectations = [
  { icon: PhoneCall, text: "One 60-minute video conversation with you or your leadership team." },
  { icon: FileText, text: "You leave with a clear picture of your top gaps and what to fix first." },
  { icon: BadgeCheck, text: "No cost and no obligation." },
];

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/care-continuity-check`, {
        ...form,
        org_type: "",
        facility_type: "",
        beds: "",
        preferred_format: "",
      });
      setDone(true);
      toast.success("Request received. We'll be in touch.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again, or reach us by phone or email below.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="bg-skybrand-50 border-b border-skybrand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <Reveal y={12}>
            <Eyebrow>Talk with us</Eyebrow>
          </Reveal>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-navy leading-[1.08]">
            <MaskedLines lines={["Tell us what's", "not working."]} />
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl font-sans text-lg sm:text-xl leading-relaxed text-slate-600">
              Tell us a little about your business, and we'll be in touch to schedule your free consultation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              {done ? (
                <Reveal>
                  <div
                    data-testid="contact-form-success-banner"
                    className="rounded-2xl border border-gold-border bg-gold-wash p-10 text-center"
                  >
                    <CheckCircle2 className="mx-auto w-12 h-12 text-gold-deep" />
                    <h2 className="mt-5 font-serif text-2xl sm:text-3xl font-semibold text-navy">Thank you.</h2>
                    <p className="mt-3 font-sans text-base sm:text-lg leading-relaxed text-slate-600">
                      We'll be in touch to schedule your free consultation.
                    </p>
                  </div>
                </Reveal>
              ) : (
                <Reveal>
                  <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="contact-form">
                    <Field label="Name" htmlFor="name">
                      <input id="name" data-testid="contact-form-name-input" required className={inputCls}
                        value={form.name} onChange={set("name")} placeholder="Your full name" />
                    </Field>
                    <Field label="Business" htmlFor="facility">
                      <input id="facility" data-testid="contact-form-facility-name" required className={inputCls}
                        value={form.facility_name} onChange={set("facility_name")} placeholder="Your business" />
                    </Field>
                    <Field label="Type of business" htmlFor="btype">
                      <select id="btype" data-testid="contact-form-business-type-select" required className={inputCls}
                        value={form.business_type} onChange={set("business_type")}>
                        <option value="" disabled>Select one</option>
                        {businessTypes.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Role" htmlFor="role">
                      <input id="role" data-testid="contact-form-role-select" required className={inputCls}
                        value={form.role} onChange={set("role")} placeholder="e.g. Owner, Manager" />
                    </Field>
                    <Field label="Email" htmlFor="email">
                      <input id="email" data-testid="contact-form-email-input" required type="email" className={inputCls}
                        value={form.email} onChange={set("email")} placeholder="you@example.com" />
                    </Field>
                    <Field label="Phone" htmlFor="phone">
                      <input id="phone" data-testid="contact-form-phone-input" required type="tel" className={inputCls}
                        value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="What's not working?" htmlFor="concern">
                        <textarea id="concern" data-testid="contact-form-notes" required rows={5}
                          className={`${inputCls} resize-y`} value={form.concern} onChange={set("concern")}
                          placeholder="Missed calls, slow replies, follow-ups that slip — whatever eats your day." />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <button
                        type="submit"
                        data-testid="contact-form-submit-button"
                        disabled={submitting}
                        className="w-full sm:w-auto inline-flex items-center justify-center rounded-full bg-gold px-8 py-4 font-sans text-base font-semibold text-navy-dark transition-all duration-300 hover:bg-gold-hover hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 shadow-[0_10px_30px_-10px_rgba(201,162,39,0.6)]"
                      >
                        {submitting ? "Sending…" : "Request My Free Consultation"}
                      </button>
                      <p className="mt-4 font-sans text-xs text-slate-500">
                        Free · No obligation · Your information stays between us.
                      </p>
                    </div>
                  </form>
                </Reveal>
              )}
            </div>

            <aside className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-2xl bg-skybrand-50 border border-skybrand-border p-8" data-testid="what-to-expect-box">
                  <h2 className="font-serif text-xl sm:text-2xl font-semibold text-navy">What to expect</h2>
                  <ul className="mt-6 space-y-5">
                    {expectations.map((e, i) => (
                      <li key={i} className="flex items-start gap-4">
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white border border-gold-border text-gold-deep">
                          <e.icon className="w-4 h-4" />
                        </span>
                        <p className="font-sans text-sm sm:text-base leading-relaxed text-slate-600">{e.text}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-6 rounded-2xl border border-slate-200 p-8">
                  <h2 className="font-serif text-xl sm:text-2xl font-semibold text-navy">Reach us directly</h2>
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link"
                    className="mt-5 flex items-center gap-3 font-sans text-sm text-slate-600 hover:text-gold-deep transition-colors duration-300 break-all">
                    <Mail className="w-4 h-4 shrink-0 text-gold-deep" /> {CONTACT.email}
                  </a>
                  <a href={CONTACT.phoneHref} data-testid="contact-phone-link"
                    className="mt-4 flex items-center gap-3 font-sans text-sm text-slate-600 hover:text-gold-deep transition-colors duration-300">
                    <Phone className="w-4 h-4 shrink-0 text-gold-deep" /> {CONTACT.phone}
                  </a>
                </div>
              </Reveal>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
